import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NetworkService } from './network.service';

/**
 * Manages offline detection UI state.
 *
 * - Shows an offline overlay only after verifying the device is truly offline
 *   (cross-checks Capacitor Network plugin with navigator.onLine).
 * - Hides automatically when connectivity is restored.
 * - Allows the user to dismiss the overlay; after dismissal the service
 *   re-checks on navigation, tab-visibility changes, and failed HTTP requests,
 *   re-surfacing the overlay if still offline.
 */
@Injectable({ providedIn: 'root' })
export class OfflineDetectionService {
  private readonly networkService = inject(NetworkService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Whether the user has dismissed the current offline overlay. */
  private readonly _dismissed = signal(false);

  /** Whether the device has been verified as truly offline. */
  private readonly _verifiedOffline = signal(false);

  /** Whether a retry check is currently in progress. */
  readonly isRetrying = signal(false);

  /** Whether the offline overlay should be displayed. */
  readonly showOfflineScreen = computed(() => this._verifiedOffline() && !this._dismissed());

  constructor() {
    effect(() => {
      const online = this.networkService.isOnline();
      if (online) {
        // Network came back — clear both flags so the overlay hides.
        this._verifiedOffline.set(false);
        this._dismissed.set(false);
      } else {
        // Capacitor reports offline — verify before showing the overlay.
        this.verifyOffline();
      }
    });

    this.listenToNavigation();
    this.listenToVisibility();
  }

  /** Dismiss the offline overlay. */
  dismiss(): void {
    this._dismissed.set(true);
  }

  /** Manually re-check connectivity and re-show if still offline. */
  async retry(): Promise<void> {
    this.isRetrying.set(true);
    try {
      const connected = await this.networkService.checkConnection();
      if (connected || navigator.onLine) {
        this._verifiedOffline.set(false);
      }
    } finally {
      this.isRetrying.set(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Re-check triggers
  // ---------------------------------------------------------------------------

  private listenToNavigation(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.recheckIfDismissed());
  }

  private listenToVisibility(): void {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        this.recheckIfDismissed();
      }
    };

    document.addEventListener('visibilitychange', handler);
    this.destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', handler));
  }

  /**
   * If the overlay was dismissed and the device is still offline,
   * re-surface the overlay. Called internally by navigation/visibility
   * triggers and externally by the offline HTTP interceptor.
   */
  async recheckIfDismissed(): Promise<void> {
    if (!this._dismissed()) {
      return;
    }

    await this.networkService.checkConnection();

    if (!this.networkService.isOnline() && !navigator.onLine) {
      this._dismissed.set(false);
    }
  }

  /**
   * Cross-check Capacitor's offline report with navigator.onLine.
   * Only mark as verified offline if both sources agree.
   */
  private async verifyOffline(): Promise<void> {
    const connected = await this.networkService.checkConnection();
    if (!connected && !navigator.onLine) {
      this._verifiedOffline.set(true);
    }
  }
}
